import { useQuery, useMutation } from '@tanstack/react-query'

import { get } from '@/lib/api'

// ==================== Types ====================

/**
 * 部门树节点
 */
export interface OrgDepartmentTreeNode {
  id: string
  name: string
  code: string
  parentId: string | null
  managerId: string | null
  managerName: string | null
  managerAvatar: string | null
  employeeCount: number
  isActive: boolean
  children: OrgDepartmentTreeNode[]
}

/**
 * 组织统计数据
 */
export interface OrganizationStatistics {
  totalEmployees: number
  activeEmployees: number
  departmentCount: number
  teamCount: number
  projectCount: number
  employeesByDepartment: {
    departmentId: string
    departmentName: string
    count: number
  }[]
  employeesByTeam: {
    teamId: string
    teamName: string
    count: number
  }[]
  employeesByRole: {
    role: string
    roleLabel: string
    count: number
  }[]
}

/**
 * 团队成员
 */
export interface TeamMemberInfo {
  id: string
  employeeId: string
  name: string
  avatar: string | null
  isLeader: boolean
}

/**
 * 团队资源视图
 */
export interface TeamResourceView {
  id: string
  name: string
  description: string | null
  leaderId: string
  leaderName: string
  leaderAvatar: string | null
  status: 'active' | 'archived'
  memberCount: number
  developerCount: number
  functionCount: number
  membersByGroup: {
    group: string
    groupLabel: string
    count: number
    members: TeamMemberInfo[]
  }[]
  projects: {
    id: string
    name: string
    status: string
  }[]
}

/**
 * 汇报关系
 */
export interface ReportingChain {
  employee: {
    id: string
    name: string
    avatar: string | null
    position: string | null
    department: string | null
    role: string
  }
  managers: {
    id: string
    name: string
    avatar: string | null
    position: string | null
    department: string | null
    level: number
  }[]
  subordinates: {
    id: string
    name: string
    avatar: string | null
    position: string | null
    department: string | null
  }[]
}

/**
 * 团队资源查询参数
 */
export interface TeamResourceParams {
  teamId?: string
  status?: 'active' | 'archived'
}

/**
 * 日期范围参数
 */
export interface DateRangeParams {
  startDate?: string
  endDate?: string
}

/**
 * 部门分布统计
 */
export interface DepartmentDistribution {
  departmentId: string
  departmentName: string
  employeeCount: number
  percentage: number
}

/**
 * 角色分布统计
 */
export interface RoleDistribution {
  role: string
  roleName: string
  employeeCount: number
}

/**
 * 团队对比统计
 */
export interface TeamComparison {
  teamId: string
  teamName: string
  leaderId: string
  leaderName: string
  memberCount: number
}

/**
 * 导出参数
 */
export interface ExportParams {
  format: 'csv' | 'excel'
  departmentId?: string
  teamId?: string
  role?: string
}

// ==================== Query Keys ====================

export const organizationKeys = {
  all: ['organization'] as const,
  departmentTree: () => [...organizationKeys.all, 'department-tree'] as const,
  statistics: () => [...organizationKeys.all, 'statistics'] as const,
  teamResources: (params?: TeamResourceParams) =>
    [...organizationKeys.all, 'team-resources', params] as const,
  reportingChain: (employeeId: string) =>
    [...organizationKeys.all, 'reporting-chain', employeeId] as const,
  departmentDistribution: (params?: DateRangeParams) =>
    [...organizationKeys.all, 'department-distribution', params] as const,
  roleDistribution: (params?: DateRangeParams) =>
    [...organizationKeys.all, 'role-distribution', params] as const,
  teamComparison: (params?: DateRangeParams) =>
    [...organizationKeys.all, 'team-comparison', params] as const,
}

// ==================== Hooks ====================

/**
 * 获取部门树形结构
 */
export function useOrgDepartmentTree() {
  return useQuery({
    queryKey: organizationKeys.departmentTree(),
    queryFn: () => get<OrgDepartmentTreeNode[]>('/organization/departments/tree'),
    staleTime: 5 * 60 * 1000, // 5 分钟
  })
}

/**
 * 获取组织统计数据
 */
export function useOrganizationStatistics() {
  return useQuery({
    queryKey: organizationKeys.statistics(),
    queryFn: () => get<OrganizationStatistics>('/organization/statistics'),
    staleTime: 10 * 60 * 1000, // 10 分钟
  })
}

/**
 * 获取团队资源视图
 */
export function useTeamResources(params?: TeamResourceParams) {
  return useQuery({
    queryKey: organizationKeys.teamResources(params),
    queryFn: () =>
      get<TeamResourceView[]>('/organization/teams/resources', params as Record<string, unknown>),
    staleTime: 5 * 60 * 1000, // 5 分钟
  })
}

/**
 * 获取员工汇报关系链
 */
export function useReportingChain(employeeId: string) {
  return useQuery({
    queryKey: organizationKeys.reportingChain(employeeId),
    queryFn: () => get<ReportingChain>(`/organization/employees/${employeeId}/reporting-chain`),
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000, // 5 分钟
  })
}

/**
 * 获取部门分布统计
 */
export function useDepartmentDistribution(params?: DateRangeParams) {
  return useQuery({
    queryKey: organizationKeys.departmentDistribution(params),
    queryFn: () =>
      get<DepartmentDistribution[]>(
        '/organization/statistics/departments',
        params as Record<string, unknown>
      ),
    staleTime: 5 * 60 * 1000, // 5 分钟
  })
}

/**
 * 获取角色分布统计
 */
export function useRoleDistribution(params?: DateRangeParams) {
  return useQuery({
    queryKey: organizationKeys.roleDistribution(params),
    queryFn: () =>
      get<RoleDistribution[]>(
        '/organization/statistics/roles',
        params as Record<string, unknown>
      ),
    staleTime: 5 * 60 * 1000, // 5 分钟
  })
}

/**
 * 获取团队对比统计
 */
export function useTeamComparison(params?: DateRangeParams) {
  return useQuery({
    queryKey: organizationKeys.teamComparison(params),
    queryFn: () =>
      get<TeamComparison[]>(
        '/organization/statistics/teams',
        params as Record<string, unknown>
      ),
    staleTime: 5 * 60 * 1000, // 5 分钟
  })
}

/**
 * 导出组织数据
 */
export function useExportOrganization() {
  return useMutation({
    mutationFn: async (params: ExportParams) => {
      const queryParams = new URLSearchParams()
      queryParams.set('format', params.format)
      if (params.departmentId) {queryParams.set('departmentId', params.departmentId)}
      if (params.teamId) {queryParams.set('teamId', params.teamId)}
      if (params.role) {queryParams.set('role', params.role)}

      const response = await fetch(`/api/v1/organization/export?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (!response.ok) {
        throw new Error('导出失败')
      }

      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
      const filename = filenameMatch?.[1] ?? `organization-${new Date().toISOString().split('T')[0]}.csv`

      // 下载文件
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      return { success: true }
    },
  })
}
